import React, { useState } from "react";
import { toast } from "sonner";
import { Heart, Sparkles } from "lucide-react";

import { saveToneProfile } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TailorSignalsPanel — soft, affirming panel shown at the top of a
 * routine when the CV pipeline couldn't confidently classify the
 * user's skin tone or undertone.
 *
 * We NEVER treat low confidence as an error — we treat it as an
 * invitation to collaborate. Copy leans on "every bit of feedback
 * makes a better app for you" per founder direction.
 *
 * Props:
 *   onRecommendationsReady(recs):
 *     Called with the `product_recommendations` map after the user
 *     submits the panel. Parent re-renders the step cards with the
 *     updated, user-tailored picks.
 */
const UNDERTONES = [
  { id: "warm", label: "Warm", hint: "golden / yellow / peach" },
  { id: "cool", label: "Cool", hint: "pink / rosy / red" },
  { id: "neutral", label: "Neutral", hint: "a balance of both" },
  { id: "olive", label: "Olive", hint: "greenish-golden" },
];

const DEPTHS = [
  { id: "light", label: "Light" },
  { id: "medium", label: "Medium" },
  { id: "deep", label: "Deep" },
];

const COVERAGES = [
  { id: "sheer", label: "Sheer" },
  { id: "medium", label: "Medium" },
  { id: "full", label: "Full" },
];

const FINISHES = [
  { id: "matte", label: "Matte" },
  { id: "satin", label: "Satin" },
  { id: "dewy", label: "Dewy" },
];

const Pill = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3.5 py-2 rounded-full text-sm transition-all border ${
      active
        ? "bg-[#9B6A6C] text-white border-[#9B6A6C]"
        : "bg-white/70 text-[#5C4B51] border-[#EADDD7] hover:border-[#D5A8A4]"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

const TailorSignalsPanel = ({ onRecommendationsReady, beardShadow = false }) => {
  const [undertone, setUndertone] = useState(null);
  const [depth, setDepth] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [finish, setFinish] = useState(null);
  const [saveProfile, setSaveProfile] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const canSubmit = undertone && depth && !submitting;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${API}/analyze/confirm-signals`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          undertone,
          skin_depth: depth,
          coverage: coverage || undefined,
          finish: finish || undefined,
          beard_shadow: beardShadow,
          goal: "everyday",
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      // Also persist as a tone profile when the user opted in. Silent
      // on failure — the main flow (recommendations) already worked.
      if (saveProfile) {
        try {
          await saveToneProfile({
            undertone,
            skin_depth: depth,
            coverage: coverage || null,
            finish: finish || null,
            apply_to_routines: true,
          });
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.debug("[tailor-signals] profile save failed:", err?.message || err);
          }
        }
      }
      toast.success(
        saveProfile
          ? "Thanks — saved to your profile for next time."
          : "Thanks — picks updated to match you.",
      );
      onRecommendationsReady?.(
        data.product_recommendations,
        data.skin_signals,
      );
      setDismissed(true);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[tailor-signals] failed:", e?.message || e);
      }
      toast.error("We couldn't update your picks. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="mb-8 rounded-3xl border border-[#EADDD7] bg-white/75 backdrop-blur-sm p-6 sm:p-7"
      data-testid="tailor-signals-panel"
    >
      <div className="flex items-start gap-3 mb-5">
        <span className="shrink-0 h-9 w-9 rounded-full bg-[#F2E8E9] flex items-center justify-center">
          <Heart className="h-4 w-4 text-[#9B6A6C]" />
        </span>
        <div>
          <h3 className="text-lg text-[#2C2123] font-medium leading-tight">
            We're not sure — help us tailor this.
          </h3>
          <p className="text-sm text-[#5C4B51] mt-1">
            Every bit of feedback makes a better app for you. Pick what feels
            right (you can always change it later).
          </p>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8A7D82] mb-2">
            Undertone
          </div>
          <div className="flex flex-wrap gap-2">
            {UNDERTONES.map((u) => (
              <Pill
                key={u.id}
                active={undertone === u.id}
                onClick={() => setUndertone(u.id)}
                testid={`tailor-undertone-${u.id}`}
              >
                {u.label}
                <span className="block text-[10px] opacity-80 font-normal -mt-0.5">
                  {u.hint}
                </span>
              </Pill>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8A7D82] mb-2">
            Skin depth
          </div>
          <div className="flex flex-wrap gap-2">
            {DEPTHS.map((d) => (
              <Pill
                key={d.id}
                active={depth === d.id}
                onClick={() => setDepth(d.id)}
                testid={`tailor-depth-${d.id}`}
              >
                {d.label}
              </Pill>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8A7D82] mb-2">
            Coverage <span className="normal-case text-[#BDAFB3]">· optional</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {COVERAGES.map((c) => (
              <Pill
                key={c.id}
                active={coverage === c.id}
                onClick={() => setCoverage(coverage === c.id ? null : c.id)}
                testid={`tailor-coverage-${c.id}`}
              >
                {c.label}
              </Pill>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8A7D82] mb-2">
            Finish <span className="normal-case text-[#BDAFB3]">· optional</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {FINISHES.map((f) => (
              <Pill
                key={f.id}
                active={finish === f.id}
                onClick={() => setFinish(finish === f.id ? null : f.id)}
                testid={`tailor-finish-${f.id}`}
              >
                {f.label}
              </Pill>
            ))}
          </div>
        </div>
      </div>

      <label
        className="flex items-start gap-3 mt-5 pt-5 border-t border-[#F5EFEB] cursor-pointer select-none"
        data-testid="tailor-signals-save-profile-label"
      >
        <input
          type="checkbox"
          checked={saveProfile}
          onChange={(e) => setSaveProfile(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-[#EADDD7] accent-[#9B6A6C]"
          data-testid="tailor-signals-save-profile-toggle"
        />
        <span className="text-sm text-[#5C4B51]">
          Save this to my profile so future routines match automatically.{" "}
          <span className="text-[#8A7D82]">You can edit or forget it anytime in Settings.</span>
        </span>
      </label>

      <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#F5EFEB]">
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-sm text-[#8A7D82] hover:text-[#5C4B51] transition-colors"
          data-testid="tailor-signals-dismiss"
        >
          Maybe later
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#805557] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="tailor-signals-submit"
        >
          <Sparkles className="h-4 w-4" />
          {submitting ? "Tailoring…" : "Tailor my picks"}
        </button>
      </div>
    </div>
  );
};

export default TailorSignalsPanel;
